import React, { useState, useRef } from 'react';
import { Box, Typography, IconButton, CircularProgress, TextareaAutosize } from '@mui/material';
import { FiSend } from 'react-icons/fi';
import { interactWithAgent } from './actions';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface MessageGroundChatBoxProps {
  agentId: string;
  authToken: string;
}

const MessageGroundChatBox: React.FC<MessageGroundChatBoxProps> = ({ agentId, authToken }) => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = async () => {
    if (!input.trim()) return;
    const userMsg: Message = { role: 'user', content: input };
    setHistory(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const res = await interactWithAgent(
        agentId,
        userMsg.content,
        history.map(({ role, content }) => ({ role, content })).concat(userMsg),
        authToken
      );
      res.data.data.answers.map((answer: string) => {
        const aiMsg: Message = { role: 'assistant', content: answer || '' };
        setHistory(prev => [...prev, aiMsg]);
      });
    } catch (err) {
      setHistory(prev => [...prev, { role: 'assistant', content: 'Error: Failed to get response.' }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  return (
    <Box sx={{ flex: 1, p: 4, overflowY: 'auto', background: '#fafbfc', minHeight: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box flex={1} display="flex" flexDirection="column" gap={3} mb={2} sx={{ overflowY: 'auto' }}>
        {history.length === 0 ? (
          <Box alignSelf="center" bgcolor="#f2eaff" px={3} py={2} borderRadius={3} maxWidth={380} boxShadow={1}>
            <Typography fontSize={16} fontWeight={500} color="#888">Start chatting with your assistant!</Typography>
          </Box>
        ) : history.map((msg, idx) => (
          <Box key={idx} display="flex" flexDirection="column" alignItems={msg.role === 'user' ? 'flex-end' : 'flex-start'}>
            <Box
              bgcolor={msg.role === 'user' ? '#d9d9d9' : '#fff'}
              px={3}
              py={2}
              borderRadius={10}
              maxWidth={380}
              boxShadow={1}
            >
              <Typography fontSize={16} fontWeight={400}>
                {msg.content}
              </Typography>
            </Box>
            <Typography
              fontSize={12}
              fontStyle="italic"
              color="#555"
              mt={0.5}
              mr={msg.role === 'user' ? 0.5 : 0}
              ml={msg.role !== 'user' ? 0.5 : 0}
              align={msg.role === 'user' ? 'right' : 'left'}
            >
              {msg.role === 'user' ? 'Sent by you' : 'Message from assistant name'}
            </Typography>
          </Box>
        ))}
        {loading && (
          <Box alignSelf="flex-start" display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} />
            <Typography fontSize={15} color="#888">Thinking...</Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          border: '1px solid #ddd',
          borderRadius: 2,
          p: 1.5,
          backgroundColor: '#fff',
          mt: 1,
        }}
      >
        {/* First row: Expanding textarea */}
        <TextareaAutosize
          minRows={1}
          maxRows={6}
          placeholder="Type a message"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (!loading) handleSend();
            }
          }}
          ref={inputRef}
          disabled={loading}
          style={{
            width: '100%',
            resize: 'none',
            border: 'none',
            outline: 'none',
            fontSize: '16px',
            fontFamily: 'inherit',
            padding: '8px 12px',
            boxSizing: 'border-box',
            backgroundColor: 'transparent',
          }}
        />

        {/* Second row: Icons and Send button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            px: 1,
          }}
        >
          {/* Left-side icons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
          </Box>

          {/* Send button */}
          <Box>
            <IconButton
              onClick={handleSend}
              disabled={loading || !input.trim()}
              sx={{
                backgroundColor: '#373FCC',
                color: '#fff',
                borderRadius: 2,
                px: 2,
                py: 1,
                '&:hover': {
                  backgroundColor: '#373FCC',
                },
              }}
            >
              <FiSend style={{ marginRight: 4 }} />
              <Typography fontSize={14} fontWeight={500}>Send</Typography>
            </IconButton>
          </Box>
        </Box>
      </Box>

    </Box>
  );
};

export default MessageGroundChatBox;
